// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  BaseDialog,
  Button,
  Stack,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Box,
  Download02Icon,
} from '@metarouter-private/mr-mui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTrackWithUser } from '@ion/api';
import useQueryAllAnalyticsFiles from 'ion-experimental/api/analytics-files/useQueryAllAnalyticsFiles';
import useQueryConsentCategories from '@ion/api/consent-categories/useQueryConsentCategories';
import isFeatureEnabled from 'app/launch-darkly/is-feature-enabled';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import FileSelectionList, { ConfirmFileList } from '../shared/FileSelectionList';
import BulkOperationProgressView from '../shared/BulkOperationProgressView';
import BulkOperationResultsView from '../shared/BulkOperationResultsView';
import useBulkBuildAndDownload from './useBulkBuildAndDownload';
import BULK_OPERATION_STATUS from '../shared/BULK_OPERATION_STATUS';

const FILE_HOSTING_TYPE_SELF = 'SELF';
const STEPS = ['Select Files', 'Confirm'];

export default function BulkBuildAndDownloadModal({ open, onClose }) {
  const featureFlags = useFlags();
  const hasConsentModule = isFeatureEnabled({ featureFlags, flagName: FEATURE_FLAGS.ecmConfigurableConsent });

  const { data: analyticsFiles, loading: loadingFiles } = useQueryAllAnalyticsFiles({
    fileHostingType: FILE_HOSTING_TYPE_SELF,
    skip: !open,
  });

  const { data: consentCategories = [], loading: loadingConsent } = useQueryConsentCategories({ skip: !open });

  const loading = loadingFiles || loadingConsent;

  const [trackBulkBuildAndDownload] = useTrackWithUser('bulk_analytics_files_built_and_downloaded');

  const {
    selectedIds,
    setSelectedIds,
    selectedFiles,
    status,
    step,
    progress,
    results,
    goToConfirm,
    goToSelect,
    buildAndDownload,
    reset,
    MODAL_STEP,
  } = useBulkBuildAndDownload({
    analyticsFiles,
    consentCategories,
    hasConsentModule,
    onComplete: trackBulkBuildAndDownload,
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleClose = () => {
    if (status !== BULK_OPERATION_STATUS.PROCESSING) {
      onClose();
    }
  };

  const handleDownloadReport = () => {
    let yaml = '# Bulk Build Report\n';
    yaml += `# Generated: ${new Date().toISOString()}\n`;
    yaml += `# Total Success: ${results.successful.length}\n`;
    yaml += `# Total Failures: ${results.failed.length}\n\n`;

    if (results.successful.length > 0) {
      yaml += '# Successfully Built Files\n';
      yaml += 'success:\n';
      results.successful.forEach(item => {
        const name = item.file?.pipeline?.name ?? 'Unknown';
        yaml += `  "${name}":\n`;
        yaml += `    writeKey: "${item.file?.pipeline?.writekey ?? 'Unknown'}"\n`;
        yaml += `    status: "built"\n\n`;
      });
    }

    if (results.failed.length > 0) {
      yaml += '# Failed Files\n';
      yaml += 'failures:\n';
      results.failed.forEach(item => {
        const name = item.item?.pipeline?.name ?? 'Unknown';
        yaml += `  "${name}":\n`;
        yaml += `    writeKey: "${item.item?.pipeline?.writekey ?? 'Unknown'}"\n`;
        yaml += `    error: "${item.error?.message ?? 'Unknown error'}"\n\n`;
      });
    }

    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.download = `bulk-build-report-${timestamp}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activeStep = step === MODAL_STEP.SELECT ? 0 : 1;
  const isProcessing = status === BULK_OPERATION_STATUS.PROCESSING;
  const isComplete = status === BULK_OPERATION_STATUS.COMPLETE;

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (isProcessing) {
      return <BulkOperationProgressView progress={progress} message="Building files..." />;
    }

    if (isComplete) {
      return <BulkOperationResultsView results={results} successLabel="Build complete" />;
    }

    if (step === MODAL_STEP.SELECT) {
      return (
        <FileSelectionList files={analyticsFiles ?? []} selectedIds={selectedIds} onSelectionChange={setSelectedIds} />
      );
    }

    const description = `You are about to build and download ${selectedFiles.length} file${
      selectedFiles.length !== 1 ? 's' : ''
    }:`;
    return <ConfirmFileList files={selectedFiles} description={description} />;
  };

  const renderActions = () => {
    if (isProcessing) {
      return null;
    }

    if (isComplete) {
      return (
        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Close
          </Button>
          <Button onClick={handleDownloadReport} variant="contained" startIcon={<Download02Icon />}>
            Download Report
          </Button>
        </Stack>
      );
    }

    if (step === MODAL_STEP.SELECT) {
      return (
        <>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={goToConfirm}
            variant="contained"
            disabled={selectedIds.size === 0}
            data-testid="bulk-build-continue-btn"
          >
            Continue
          </Button>
        </>
      );
    }

    return (
      <>
        <Button onClick={goToSelect} color="secondary" variant="outlined">
          Back
        </Button>
        <Button onClick={buildAndDownload} variant="contained" data-testid="bulk-build-submit-btn">
          Build & Download
        </Button>
      </>
    );
  };

  return (
    <BaseDialog
      DialogProps={{
        open,
        maxWidth: 'sm',
        fullWidth: true,
        onClose: handleClose,
        'data-test-id': 'bulk-build-download-modal',
      }}
      title="Bulk Build & Download"
      content={
        <Stack spacing={3}>
          {!isProcessing && !isComplete && (
            <Stepper activeStep={activeStep} sx={{ pt: 1 }}>
              {STEPS.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          {renderContent()}
        </Stack>
      }
      actions={renderActions()}
    />
  );
}

BulkBuildAndDownloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
